"use strict";
/**
* Scheduler.ts
* Author: CALIB http://aka.ms/CALIB
* Copyright: Microsoft 2017
*
* Scheduling module for fetching ECS config
*/
Object.defineProperty(exports, "__esModule", { value: true });
var Models_1 = require("./Models");
var RestClient_1 = require("./RestClient");
var Scheduler = /** @class */ (function () {
    function Scheduler(_config, _skypeTokenData, _cache, appActiveData, allowBackgroundFetchData, telemetryManager) {
        var _this = this;
        this._config = _config;
        this._skypeTokenData = _skypeTokenData;
        this._cache = _cache;
        this._isFetchingSettings = {};
        this._pendingFetch = {};
        this._paused = false;
        this._fetchEcsSettingsIfNeeded = function (configType) {
            var config = _this._config.getConfig();
            var cachedConfig = _this._cache.getEcsConfigByType(configType);
            // If we don't care about this config, abort
            if (config.configsToFetch.indexOf(configType) === -1) {
                return;
            }
            // Don't refresh if we're already fetching.
            if (_this._isFetchingSettings[configType]) {
                return;
            }
            if (!cachedConfig && _this._pendingFetch[configType]) {
                _this._fetchSkypeEcsSettings(configType);
                return;
            }
            // Get time until expiration, negative if we have no config.
            var configExpirationMs = cachedConfig ? cachedConfig.expiration - Date.now() : -1;
            // If config expired, fetch now.
            if (configExpirationMs <= 0) {
                _this._fetchSkypeEcsSettings(configType);
                return;
            }
            // If SkypeToken used to fetch has changed, fetchNow
            if (cachedConfig && configType === Models_1.EcsConfigType.User && _this._skypeTokenData.isSkypeTokenValid() &&
                cachedConfig.lastFetchTokenHash !== _this._skypeTokenData.getSkypeTokenHash(_this._skypeTokenData.getSkypeToken())) {
                _this._fetchSkypeEcsSettings(configType);
                return;
            }
            // Schedule another check.
            setTimeout(function () { return _this._fetchEcsSettingsIfNeeded(configType); }, configExpirationMs);
        };
        this._fetchSkypeEcsSettings = function (configType, bypassPause) {
            if (bypassPause === void 0) { bypassPause = false; }
            if (_this._isFetchingSettings[configType] || (_this._paused && !bypassPause)) {
                _this._pendingFetch[configType] = true;
                return;
            }
            _this._pendingFetch[configType] = false;
            _this._isFetchingSettings[configType] = true;
            var config = _this._config.getConfig();
            // will never fail. We'll keep trying to fetch with exponential backoff until the end of time.
            config.getEcsParameters()
                .catch(function () {
                // If we fail to get parameters, continue with none
                console.warn('ECS - Failed to fetch ECS fetching parameters');
                return {};
            })
                .then(function (parameters) {
                return _this._ecsClient
                    .getConfig(_this._cache.getEcsConfig(), configType === Models_1.EcsConfigType.User, parameters);
            })
                .then(function (ecsConfig) {
                console.log('ECS - Config fetch complete');
                _this._isFetchingSettings[configType] = false;
                _this._cache.putConfig(ecsConfig);
                // Queue a refresh
                _this._fetchEcsSettingsIfNeeded(configType);
            });
        };
        this._ecsClient = new RestClient_1.default(this._config, this._skypeTokenData, appActiveData, allowBackgroundFetchData, telemetryManager);
    }
    Scheduler.prototype.initialize = function () {
        var _this = this;
        this.updateConfigsToFetch();
        // Re-fetch user settings when skype token changes
        this._skypeTokenData.skypeTokenChanged.subscribe(function () { return _this._fetchEcsSettingsIfNeeded(Models_1.EcsConfigType.User); });
    };
    Scheduler.prototype.updateConfigsToFetch = function () {
        var config = this._config.getConfig();
        for (var _i = 0, _a = config.configsToFetch; _i < _a.length; _i++) {
            var configType = _a[_i];
            if (!(configType in this._isFetchingSettings)) {
                this._isFetchingSettings[configType] = false;
            }
            if (!(configType in this._isFetchingSettings)) {
                this._pendingFetch[configType] = false;
            }
        }
        // If we haven't downloaded a config yet (which should happen only on
        // first run), download it immediately so we have the settings by the time
        // the UI loads, and we can enable or disable specific UI features.
        for (var _b = 0, _c = config.configsToFetch; _b < _c.length; _b++) {
            var configType = _c[_b];
            var cachedData = this._cache.getEcsConfigByType(configType);
            if (!cachedData || (cachedData.appVersion && cachedData.appVersion !== config.clientVersion)) {
                this._fetchSkypeEcsSettings(configType, true);
            }
            else {
                this._fetchEcsSettingsIfNeeded(configType);
            }
        }
    };
    Scheduler.prototype.requestUpdate = function () {
        var config = this._config.getConfig();
        for (var _i = 0, _a = config.configsToFetch; _i < _a.length; _i++) {
            var configType = _a[_i];
            this._fetchSkypeEcsSettings(configType);
        }
    };
    Scheduler.prototype.pause = function () {
        this._paused = true;
    };
    Scheduler.prototype.resume = function () {
        this._paused = false;
        var config = this._config.getConfig();
        for (var _i = 0, _a = config.configsToFetch; _i < _a.length; _i++) {
            var configType = _a[_i];
            this._fetchEcsSettingsIfNeeded(configType);
        }
    };
    return Scheduler;
}());
exports.default = Scheduler;
